import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import {
  confirmFinancing,
  fetchExpenseCategories,
  fetchHouseMembers,
  homeExpenseQueryKeys,
  previewFinancing,
} from "@apps/dashboard/lib/home-expense-api";
import type { FinancingPreviewResponse, FinancingUpsertPayload } from "@apps/dashboard/lib/home-expense-types";
import {
  PAID_BY_JOINT_SELECT_VALUE,
  paidByPayloadFromSelectValue,
} from "@apps/dashboard/lib/paid-by-select";
import { parseAmountToMinor } from "@apps/dashboard/lib/parse-amount";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { FilePondStorageUpload } from "@apps/dashboard/components/FilePondStorageUpload";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { formatShort } from "@project/ui";
import { authClient } from "@apps/dashboard/lib/auth-client";

const fieldClass =
  "mt-2 w-full rounded-xl border-none bg-surface-container-low px-4 py-4 font-body text-on-surface outline-none transition-all duration-300 focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary";
const labelClass =
  "ml-1 block text-[0.7rem] font-bold uppercase tracking-widest text-on-surface-variant";

function todayLocalIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function FinancingNewPage(): JSX.Element {
  const { houseId } = useParams<{ houseId: string }>();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { houses } = useActiveHouse();
  const { data: session } = authClient.useSession();
  const currentUserId = session?.user?.id ?? "";
  const actorRole = houseId ? houses?.find((h) => h.id === houseId)?.role : undefined;
  const canEdit = actorRole === "owner" || actorRole === "admin";
  useSyncHouseFromParams(houseId);

  const [step, setStep] = useState<0 | 1>(0);
  const [title, setTitle] = useState("");
  const [lender, setLender] = useState("");
  const [amountInput, setAmountInput] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [contractStartOn, setContractStartOn] = useState(todayLocalIsoDate());
  const [endsOn, setEndsOn] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [notes, setNotes] = useState("");
  const [docKeys, setDocKeys] = useState<string[]>([]);
  const [runAiAnalysis, setRunAiAnalysis] = useState(false);
  const [generateRecurring, setGenerateRecurring] = useState(false);
  const [paidBySelect, setPaidBySelect] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"" | "card" | "cash" | "account">("");
  const [preview, setPreview] = useState<FinancingPreviewResponse | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const categoriesQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.categories(locale, houseId) : ["cat", "x"],
    queryFn: () => {
      if (!houseId) throw new Error("no house");
      return fetchExpenseCategories(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const membersQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.houseMembers(locale, houseId) : ["mem", "x"],
    queryFn: () => {
      if (!houseId) throw new Error("no house");
      return fetchHouseMembers(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const categories = categoriesQuery.data ?? [];
  const members = membersQuery.data ?? [];

  function buildPayload(): FinancingUpsertPayload {
    const minor = parseAmountToMinor(amountInput.trim());
    if (minor === null) {
      throw new Error("amount");
    }
    const payer =
      generateRecurring && paidBySelect !== PAID_BY_JOINT_SELECT_VALUE
        ? paidByPayloadFromSelectValue(paidBySelect, {
            explicitDefaultPayerUserId: currentUserId || undefined,
          })
        : paidByPayloadFromSelectValue(paidBySelect);
    return {
      title: title.trim(),
      lender: lender.trim() || null,
      installmentAmountMinor: minor,
      currency,
      contractStartOn,
      endsOn,
      categoryId: categoryId || null,
      notes: notes.trim() || null,
      documents: docKeys.map((storageKey) => ({ storageKey })),
      runAiAnalysis: runAiAnalysis && docKeys.length > 0,
      generateRecurringExpense: generateRecurring,
      paidFromJointAccount: payer.paidFromJointAccount,
      paidByUserId: payer.paidByUserId ?? null,
      paymentMethod: paymentMethod === "" ? null : paymentMethod,
      splitSource: "house",
    };
  }

  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!houseId) throw new Error("no house");
      return previewFinancing(locale, houseId, buildPayload());
    },
    onSuccess: (data) => {
      setPreview(data);
      setStep(1);
      setFormError(null);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiRequestError) {
        setFormError(e.message);
        return;
      }
      setFormError(t("dashboardUi.states.errorHint"));
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!houseId) throw new Error("no house");
      return confirmFinancing(locale, houseId, buildPayload());
    },
    onSuccess: async (res) => {
      if (!houseId) return;
      await queryClient.invalidateQueries({ queryKey: homeExpenseQueryKeys.financingsList(locale, houseId) });
      navigate(dashboardPaths.houseFinancingDetail(houseId, res.financing.id), { replace: true });
    },
    onError: (e: unknown) => {
      if (e instanceof ApiRequestError) {
        setFormError(e.message);
        return;
      }
      setFormError(t("dashboardUi.states.errorHint"));
    },
  });

  if (!houseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <DashboardBackLink to={dashboardPaths.houseFinancings(houseId)}>
          {t("dashboardUi.financings.pageTitle")}
        </DashboardBackLink>
        <p className="mt-4 text-body-md text-on-surface-variant">{t("dashboardUi.vault.readOnlyHint")}</p>
      </div>
    );
  }

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.houseFinancings(houseId)}>
        {t("dashboardUi.financings.pageTitle")}
      </DashboardBackLink>

      <h1 className="mt-4 font-headline text-headline-sm font-bold text-primary">
        {t("dashboardUi.pages.financingNew")}
      </h1>

      <div className="mt-2 flex gap-2 text-label-sm text-on-surface-variant">
        <span className={step === 0 ? "font-bold text-primary" : ""}>{t("dashboardUi.financings.stepDataTitle")}</span>
        <span aria-hidden>→</span>
        <span className={step === 1 ? "font-bold text-primary" : ""}>{t("dashboardUi.financings.stepReviewTitle")}</span>
      </div>

      {formError ? (
        <p className="mt-4 rounded-xl bg-error-container/30 px-4 py-3 text-body-sm text-error" role="alert">
          {formError}
        </p>
      ) : null}

      {step === 0 ? (
        <form
          className="mt-8 space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            setFormError(null);
            if (!title.trim()) {
              setFormError(t("validation.required"));
              return;
            }
            if (!endsOn.trim()) {
              setFormError(t("validation.required"));
              return;
            }
            const minor = parseAmountToMinor(amountInput.trim());
            if (minor === null || minor < 0) {
              setFormError(t("dashboardUi.expenses.form.invalidAmount"));
              return;
            }
            void previewMutation.mutateAsync();
          }}
        >
          <div>
            <label className={labelClass} htmlFor="fin-title">
              {t("dashboardUi.financings.titleLabel")}
            </label>
            <input id="fin-title" className={fieldClass} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label className={labelClass} htmlFor="fin-lender">
              {t("dashboardUi.financings.lenderLabel")}
            </label>
            <input id="fin-lender" className={fieldClass} value={lender} onChange={(e) => setLender(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="fin-amount">
                {t("dashboardUi.financings.installmentLabel")}
              </label>
              <input
                id="fin-amount"
                className={fieldClass}
                inputMode="decimal"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="fin-ccy">
                {t("dashboardUi.expenses.form.currencyLabel")}
              </label>
              <input id="fin-ccy" className={fieldClass} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="fin-start">
                {t("dashboardUi.financings.contractStartLabel")}
              </label>
              <input
                id="fin-start"
                type="date"
                className={fieldClass}
                value={contractStartOn}
                onChange={(e) => setContractStartOn(e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="fin-end">
                {t("dashboardUi.financings.endsOnLabel")}
              </label>
              <input id="fin-end" type="date" className={fieldClass} value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelClass} htmlFor="fin-cat">
              {t("dashboardUi.financings.categoryLabel")}
            </label>
            <select id="fin-cat" className={fieldClass} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">{t("dashboardUi.financings.categoryNone")}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass} htmlFor="fin-notes">
              {t("dashboardUi.financings.notesLabel")}
            </label>
            <textarea id="fin-notes" className={fieldClass} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div>
            <p className={labelClass}>{t("dashboardUi.vault.documentKeyLabel")}</p>
            <p className="mt-1 text-body-sm text-on-surface-variant">{t("dashboardUi.financings.docsHint")}</p>
            <div className="mt-3">
              <FilePondStorageUpload
                locale={locale}
                mode="vault"
                maxFiles={5}
                onStoredKeys={(keys) => setDocKeys(keys)}
              />
            </div>
          </div>

          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 size-4 rounded border-outline-variant"
              checked={runAiAnalysis}
              disabled={docKeys.length === 0}
              onChange={(e) => setRunAiAnalysis(e.target.checked)}
            />
            <span>
              <span className="font-label text-label-md text-on-surface">{t("dashboardUi.financings.runAiLabel")}</span>
              <span className="mt-1 block text-body-sm text-on-surface-variant">{t("dashboardUi.financings.runAiHint")}</span>
            </span>
          </label>

          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 size-4 rounded border-outline-variant"
              checked={generateRecurring}
              onChange={(e) => setGenerateRecurring(e.target.checked)}
            />
            <span>
              <span className="font-label text-label-md text-on-surface">{t("dashboardUi.financings.generateRecurringLabel")}</span>
              <span className="mt-1 block text-body-sm text-on-surface-variant">{t("dashboardUi.financings.generateRecurringHint")}</span>
            </span>
          </label>

          {generateRecurring ? (
            <div className="space-y-4 rounded-2xl border border-outline-variant/20 p-4">
              <div>
                <label className={labelClass} htmlFor="fin-paidby">
                  {t("dashboardUi.expenses.form.paidByLabel")}
                </label>
                <select id="fin-paidby" className={fieldClass} value={paidBySelect} onChange={(e) => setPaidBySelect(e.target.value)}>
                  <option value="">{t("dashboardUi.expenses.form.paidByDefault")}</option>
                  <option value={PAID_BY_JOINT_SELECT_VALUE}>{t("dashboardUi.expenses.form.paidByJointOption")}</option>
                  {members.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.user.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="fin-pm">
                  {t("dashboardUi.expenses.form.paymentMethodLabel")}
                </label>
                <select
                  id="fin-pm"
                  className={fieldClass}
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as typeof paymentMethod)}
                >
                  <option value="">{t("dashboardUi.expenses.form.paymentMethodNone")}</option>
                  <option value="card">{t("dashboardUi.expenses.form.paymentMethodCard")}</option>
                  <option value="cash">{t("dashboardUi.expenses.form.paymentMethodCash")}</option>
                  <option value="account">{t("dashboardUi.expenses.form.paymentMethodAccount")}</option>
                </select>
              </div>
            </div>
          ) : null}

          <Button type="submit" disabled={previewMutation.isPending}>
            {previewMutation.isPending ? t("dashboardUi.states.loadingHint") : t("dashboardUi.financings.continueCta")}
          </Button>
        </form>
      ) : (
        <div className="mt-8 space-y-6">
          {preview ? (
            <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 space-y-4">
              <p className="text-body-md text-on-surface">
                {t("dashboardUi.financings.reviewCutoff", { date: formatShort(`${preview.cutoffOn}T12:00:00.000Z`) })}
              </p>
              {preview.firstFutureInstallmentOn ? (
                <p className="text-body-md text-on-surface">
                  {t("dashboardUi.financings.reviewFirstPayment", {
                    date: formatShort(`${preview.firstFutureInstallmentOn}T12:00:00.000Z`),
                  })}
                </p>
              ) : null}
              <p className="text-body-md text-on-surface">
                {t("dashboardUi.financings.reviewFutureCount", { count: preview.futureInstallmentCount })}
              </p>
              <div>
                <p className="font-label text-label-md text-on-surface-variant">{t("dashboardUi.financings.reviewPreviewDates")}</p>
                <ul className="mt-2 list-inside list-disc text-body-sm text-on-surface">
                  {preview.previewInstallmentDates.map((d) => (
                    <li key={d}>{formatShort(`${d}T12:00:00.000Z`)}</li>
                  ))}
                </ul>
              </div>
              <p className="text-body-md text-on-surface">
                {preview.willGenerateRecurring ? t("dashboardUi.financings.reviewWillSeries") : t("dashboardUi.financings.reviewNoSeries")}
              </p>
              <p className="text-body-md text-on-surface">
                {preview.willRunAiAnalysis ? t("dashboardUi.financings.reviewWillAi") : t("dashboardUi.financings.aiSkipped")}
              </p>
              {preview.warningKeys.length > 0 ? (
                <ul className="list-inside list-disc text-body-sm text-amber-800 dark:text-amber-200">
                  {preview.warningKeys.map((k) => (
                    <li key={k}>{t(k)}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setStep(0);
                setPreview(null);
              }}
            >
              {t("dashboardUi.financings.backCta")}
            </Button>
            <Button
              type="button"
              disabled={confirmMutation.isPending}
              onClick={() => {
                setFormError(null);
                void confirmMutation.mutateAsync();
              }}
            >
              {confirmMutation.isPending ? t("dashboardUi.financings.saving") : t("dashboardUi.financings.confirmCta")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
